import {
  CardButton,
  type CardButtonProps,
} from '@jouzen/ecom-components/v3/CardButton';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import type { MouseEventHandler } from 'react';
import type { MessageKey } from 'types/LocaleMessageKey';

import RichText from '../RichText';

export interface ArticleCardDataProps {
  readonly articleSource?: {
    readonly date: MessageKey;
    readonly siteName?: string;
  };
  readonly buttonProps: CardButtonProps;
  readonly className?: string | undefined;
  readonly heading: MessageKey;
  readonly headingClassName?: string;
}

interface ArticleCardProps extends ArticleCardDataProps {
  readonly onClick?: MouseEventHandler<HTMLAnchorElement>;
}

const ArticleCard = ({
  articleSource,
  buttonProps,
  className = 'bg-sandstone-200 w-[75vw] md:w-full',
  heading,
  headingClassName = 'mb-36 text-heading-sm md:mb-12',
  onClick,
}: ArticleCardProps) => {
  const t = useTranslations();
  const { ariaLabel, siteName, ...rest } = buttonProps;

  return (
    <article
      className={cx(
        'flex h-full flex-col justify-between overflow-hidden rounded-lg p-6',
        className,
      )}
    >
      <Typography
        className={headingClassName}
        color="inherit"
        Element="h2"
        height="tight"
        variant="unset"
      >
        <RichText messageKey={heading} />
      </Typography>
      <div
        className={cx('flex items-end', {
          'justify-end': !articleSource,
          'justify-between': articleSource,
        })}
      >
        {articleSource && (
          <div className="flex flex-col">
            {articleSource.siteName && (
              <Typography variant="unset" className="text-xl">
                {articleSource.siteName}
              </Typography>
            )}
            <Typography variant="unset" className="text-xl">
              {t(articleSource.date)}
            </Typography>
          </div>
        )}
        <CardButton
          aria-label={t(ariaLabel)}
          data-site-name={siteName}
          {...rest}
          onClick={onClick}
        />
      </div>
    </article>
  );
};

export default ArticleCard;
